import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Alert, Button, PageLayout } from '@sentinel/ui';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';
import { useMcpServerInfo } from './use-mcp-server-info';

const styles = stylex.create({
  stack: { display: 'grid', gap: '1rem', maxWidth: '40rem' },
  card: {
    display: 'grid',
    gap: '0.875rem',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.625rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  title: { margin: 0, fontSize: '1.0625rem', fontWeight: 600 },
  text: { margin: 0, fontSize: '0.875rem', lineHeight: 1.55, color: sentinel.textMuted },
  urlRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' },
  url: {
    flexGrow: 1,
    flexBasis: '16rem',
    minWidth: 0,
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 40%, transparent)`,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.875rem',
    overflowWrap: 'anywhere',
    userSelect: 'all',
  },
  list: {
    margin: 0,
    paddingLeft: '1.25rem',
    display: 'grid',
    gap: '0.5rem',
    fontSize: '0.9375rem',
    lineHeight: 1.55,
  },
  scopes: { margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.5rem' },
  scope: { display: 'grid', gap: '0.125rem', fontSize: '0.9375rem' },
  scopeId: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  link: { color: 'inherit', textDecorationThickness: '1px', textUnderlineOffset: '0.2em' },
});

// Scopes this page can describe. Anything else the server lists is shown as
// its raw identifier rather than hidden.
const KNOWN_SCOPES: Record<string, string> = {
  'mcp:read': 'connect.scope_read',
  'mcp:write': 'connect.scope_write',
};

// How long the "Copied" / "Copy failed" outcome stays before the button resets.
const COPY_RESULT_MS = 4000;

export function McpConnectPage() {
  const { t } = useTranslation('mcp');
  const info = useMcpServerInfo();
  const data = info.data;
  // The outcome is tied to the address it was for, so a refetch that returns a
  // different resource drops it instead of showing "Copied" beside a new address.
  const [copyResult, setCopyResult] = useState<{
    resource: string;
    ok: boolean;
  } | null>(null);
  const currentResource = data?.enabled ? data.resource : null;
  const copied = copyResult?.ok === true && copyResult.resource === currentResource;
  const failed = copyResult?.ok === false && copyResult.resource === currentResource;

  useEffect(() => {
    if (!copyResult) return;
    const timer = setTimeout(() => setCopyResult(null), COPY_RESULT_MS);
    return () => clearTimeout(timer);
  }, [copyResult]);

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopyResult({ resource: url, ok: true });
    } catch {
      // Rejected, or navigator.clipboard is missing (insecure context, denied
      // permission, frame without clipboard-write). Tell the user to copy by hand.
      setCopyResult({ resource: url, ok: false });
    }
  }

  return (
    <PageLayout title={t('connect.title')} subtitle={t('connect.subtitle')}>
      {info.isPending ? (
        <p {...stylex.props(styles.text)} data-testid="mcp-connect-loading">
          {t('connect.loading')}
        </p>
      ) : null}

      {info.isError && !data ? (
        <Alert type="error" role="alert" data-testid="mcp-connect-error">
          {t('connect.error')}
        </Alert>
      ) : null}

      {data && !data.enabled ? (
        <section {...stylex.props(styles.card)} data-testid="mcp-connect-unavailable">
          <h2 {...stylex.props(styles.title)}>{t('connect.unavailable_title')}</h2>
          <p {...stylex.props(styles.text)}>{t('connect.unavailable_body')}</p>
        </section>
      ) : null}

      {data?.enabled ? (
        <div {...stylex.props(styles.stack)}>
          <section {...stylex.props(styles.card)} aria-labelledby="mcp-connect-url-title">
            <h2 id="mcp-connect-url-title" {...stylex.props(styles.title)}>
              {t('connect.url_title')}
            </h2>
            <div {...stylex.props(styles.urlRow)}>
              <code {...stylex.props(styles.url)} data-testid="mcp-connect-url">
                {data.resource}
              </code>
              <Button
                type="button"
                variant="primary"
                data-testid="mcp-connect-copy"
                onClick={() => void copy(data.resource)}
              >
                {copied ? t('connect.copied') : failed ? t('connect.copy_failed') : t('connect.copy')}
              </Button>
              <span
                role="status"
                aria-live="polite"
                data-testid="mcp-connect-copy-status"
                {...stylex.props(failed ? styles.text : styles.srOnly)}
              >
                {copied
                  ? t('connect.copied_announce')
                  : failed
                    ? t('connect.copy_failed_announce')
                    : ''}
              </span>
            </div>
          </section>

          <section {...stylex.props(styles.card)} aria-labelledby="mcp-connect-steps-title">
            <h2 id="mcp-connect-steps-title" {...stylex.props(styles.title)}>
              {t('connect.steps_title')}
            </h2>
            <p {...stylex.props(styles.text)}>{t('connect.steps_intro')}</p>
            <ol {...stylex.props(styles.list)} data-testid="mcp-connect-steps">
              <li>{t('connect.step_add')}</li>
              <li>{t('connect.step_sign_in')}</li>
              <li>{t('connect.step_consent')}</li>
            </ol>
          </section>

          {data.scopes.length > 0 ? (
            <section {...stylex.props(styles.card)} aria-labelledby="mcp-connect-scopes-title">
              <h2 id="mcp-connect-scopes-title" {...stylex.props(styles.title)}>
                {t('connect.scopes_title')}
              </h2>
              <ul {...stylex.props(styles.scopes)}>
                {data.scopes.map((scope) => {
                  const key = KNOWN_SCOPES[scope];
                  return (
                    <li
                      key={scope}
                      {...stylex.props(styles.scope)}
                      data-testid={`mcp-connect-scope-${scope}`}
                    >
                      {key ? <span>{t(key)}</span> : null}
                      <span {...stylex.props(styles.scopeId)}>{scope}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <section {...stylex.props(styles.card)} aria-labelledby="mcp-connect-manage-title">
            <h2 id="mcp-connect-manage-title" {...stylex.props(styles.title)}>
              {t('connect.manage_title')}
            </h2>
            <p {...stylex.props(styles.text)}>{t('connect.manage_body')}</p>
            <p {...stylex.props(styles.text)}>
              <Link
                to="/account/connected-apps"
                {...stylex.props(styles.link)}
                data-testid="mcp-connect-connected-apps"
              >
                {t('connect.connected_apps_link')}
              </Link>
            </p>
          </section>
        </div>
      ) : null}
    </PageLayout>
  );
}
